import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Plus, CalendarDays, Flag, ListTree, Settings2, CheckCircle2, Circle, LayoutGrid } from "lucide-react";
import { TASK_STATUSES, PRIORITIES, PRIORITY_MAP, isOverdue, fmtDate, todayStr } from "@/lib/taskConstants";
import { UserAvatar } from "@/components/Avatars";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadUrl } from "@/pages/FilesPage";

const GROUP_OPTS = [
  { value: "status", label: "Estado" },
  { value: "assignee", label: "Responsable" },
  { value: "priority", label: "Prioridad" },
  { value: "parent", label: "Tarea padre" },
  { value: "due", label: "Fecha límite" },
];

const loadOpts = () => {
  try { return { subtasks: false, description: false, image: false, ...JSON.parse(localStorage.getItem("atlas_kanban_opts")) }; }
  catch { return { subtasks: false, description: false, image: false }; }
};

const KanbanCard = ({ task, subtasks, members, onSelect, index, opts, imageFile, dragDisabled = false }) => {
  const assignee = members.find((m) => m.profile_id === task.assignee_id);
  const overdue = isOverdue(task);
  const doneSubs = subtasks.filter((s) => s.status === "done").length;

  return (
    <Draggable draggableId={task.id} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
          onClick={() => onSelect(task.id)}
          data-testid={`kanban-card-${task.id}`}
          className={`bg-card border rounded-lg p-3 space-y-2 cursor-pointer hover:border-primary/50 transition-colors ${
            snapshot.isDragging ? "shadow-lg rotate-1" : ""
          }`}>
          {opts.image && imageFile && (
            <img src={downloadUrl(imageFile.id)} alt={imageFile.name} loading="lazy"
              data-testid={`kanban-card-image-${task.id}`}
              className="w-full h-24 object-cover rounded-md -mt-0.5" />
          )}
          <p className="text-sm font-medium leading-snug">{task.title}</p>
          {opts.description && task.description && (
            <p className="text-xs text-muted-foreground line-clamp-2" data-testid={`kanban-card-desc-${task.id}`}>
              {task.description}
            </p>
          )}
          {opts.subtasks && subtasks.length > 0 && (
            <div className="space-y-0.5" data-testid={`kanban-card-subtasks-${task.id}`}>
              {subtasks.slice(0, 4).map((s) => (
                <div key={s.id} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {s.status === "done"
                    ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                    : <Circle className="h-3 w-3 shrink-0" />}
                  <span className={`truncate ${s.status === "done" ? "line-through" : ""}`}>{s.title}</span>
                </div>
              ))}
              {subtasks.length > 4 && (
                <span className="text-[10px] text-muted-foreground">+{subtasks.length - 4} más</span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Flag className="h-3 w-3" style={{ color: PRIORITY_MAP[task.priority]?.color }} />
            {task.due_date && (
              <span className={`text-[11px] flex items-center gap-1 ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                <CalendarDays className="h-3 w-3" />{fmtDate(task.due_date)}
              </span>
            )}
            {task.reward_amount > 0 && (
              <span className={`text-[11px] flex items-center gap-0.5 font-medium ${task.reward_paid_at ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}
                data-testid={`kanban-card-reward-${task.id}`}>
                💰{task.reward_amount}€
              </span>
            )}
            {subtasks.length > 0 && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <ListTree className="h-3 w-3" />{doneSubs}/{subtasks.length}
              </span>
            )}
            <span className="flex-1" />
            {assignee && (
              <UserAvatar name={assignee.name} color={assignee.avatar_color} src={assignee.avatar_url} size="sm" className="!h-5 !w-5 !text-[9px]" />
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

export const KanbanView = ({ tasks, subtasksByParent, members, onSelect, onMove, onUpdateField, onQuickCreate, imagesByTask = {} }) => {
  const [quickAdd, setQuickAdd] = useState(null);
  const [quickTitle, setQuickTitle] = useState("");
  const [opts, setOpts] = useState(loadOpts);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("atlas_kanban_group") || "status");

  const toggleOpt = (key) => {
    const next = { ...opts, [key]: !opts[key] };
    setOpts(next);
    localStorage.setItem("atlas_kanban_opts", JSON.stringify(next));
  };
  const changeGroup = (v) => {
    setGroupBy(v);
    localStorage.setItem("atlas_kanban_group", v);
  };

  const sorted = (arr) => [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const today = todayStr();
  const in7 = new Date(); in7.setDate(in7.getDate() + 7);
  const week = in7.toISOString().slice(0, 10);

  let columns = [];
  if (groupBy === "status") {
    columns = TASK_STATUSES.map((s) => ({
      key: s.value, label: s.label, color: s.color,
      items: sorted(tasks.filter((t) => t.status === s.value)),
    }));
  } else if (groupBy === "assignee") {
    const active = members.filter((m) => m.status === "active");
    columns = [
      ...active.map((m) => ({
        key: m.profile_id, label: m.name, color: m.avatar_color,
        items: sorted(tasks.filter((t) => t.assignee_id === m.profile_id)),
      })),
      { key: "__none__", label: "Sin responsable", color: "#9AA0A6",
        items: sorted(tasks.filter((t) => !t.assignee_id || !active.some((m) => m.profile_id === t.assignee_id))) },
    ].filter((c) => c.key === "__none__" || c.items.length > 0 || active.length <= 8);
  } else if (groupBy === "priority") {
    columns = PRIORITIES.map((p) => ({
      key: p.value, label: p.label, color: p.color,
      items: sorted(tasks.filter((t) => t.priority === p.value)),
    }));
  } else if (groupBy === "parent") {
    columns = tasks.map((t) => ({
      key: t.id, label: t.title, color: isOverdue(t) ? "#D93025" : TASK_STATUSES.find((s) => s.value === t.status)?.color,
      items: sorted(subtasksByParent[t.id] || []), parentTask: t,
    }));
  } else {
    const buckets = [
      { key: "overdue", label: "Vencidas", color: "#D93025", test: (t) => t.due_date && t.due_date < today && t.status !== "done" },
      { key: "today", label: "Hoy", color: "#F9AB00", test: (t) => t.due_date === today },
      { key: "week", label: "Próximos 7 días", color: "#1A73E8", test: (t) => t.due_date && t.due_date > today && t.due_date <= week },
      { key: "later", label: "Más adelante", color: "#1E8E3E", test: (t) => t.due_date && t.due_date > week },
      { key: "none", label: "Sin fecha", color: "#9AA0A6", test: (t) => !t.due_date },
    ];
    const used = new Set();
    columns = buckets.map((b) => ({
      key: b.key, label: b.label, color: b.color,
      items: sorted(tasks.filter((t) => !used.has(t.id) && b.test(t) && used.add(t.id) !== false)),
    }));
  }

  const dragEnabled = ["status", "assignee", "priority"].includes(groupBy);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    if (groupBy === "status") {
      const destCol = columns.find((c) => c.key === destination.droppableId);
      const destList = destCol.items.filter((t) => t.id !== draggableId);
      const prev = destList[destination.index - 1];
      const next = destList[destination.index];
      let order;
      if (prev && next) order = ((prev.order ?? 0) + (next.order ?? 0)) / 2;
      else if (prev) order = (prev.order ?? 0) + 1;
      else if (next) order = (next.order ?? 0) - 1;
      else order = 0;
      onMove(draggableId, destination.droppableId, order);
    } else if (groupBy === "assignee") {
      onUpdateField(draggableId, { assignee_id: destination.droppableId === "__none__" ? null : destination.droppableId });
    } else if (groupBy === "priority") {
      onUpdateField(draggableId, { priority: destination.droppableId });
    }
  };

  const submitQuick = (status) => {
    if (quickTitle.trim()) onQuickCreate(quickTitle.trim(), status);
    setQuickTitle("");
    setQuickAdd(null);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="space-y-3">
        <div className="flex items-center justify-end gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LayoutGrid className="h-3.5 w-3.5" /> Agrupar por
          </div>
          <Select value={groupBy} onValueChange={changeGroup}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="kanban-groupby-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GROUP_OPTS.map((g) => <SelectItem key={g.value} value={g.value} data-testid={`kanban-groupby-${g.value}`}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="kanban-card-options-btn">
                <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Tarjetas
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs">Mostrar en las tarjetas</DropdownMenuLabel>
              <DropdownMenuCheckboxItem checked={opts.subtasks} onCheckedChange={() => toggleOpt("subtasks")}
                onSelect={(e) => e.preventDefault()} data-testid="kanban-opt-subtasks">
                Lista de subtareas
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={opts.description} onCheckedChange={() => toggleOpt("description")}
                onSelect={(e) => e.preventDefault()} data-testid="kanban-opt-description">
                Descripción
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem checked={opts.image} onCheckedChange={() => toggleOpt("image")}
                onSelect={(e) => e.preventDefault()} data-testid="kanban-opt-image">
                Imagen adjunta
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex gap-4 items-start overflow-x-auto pb-2" data-testid="kanban-view">
        {columns.map((col) => (
          <div key={col.key} className="bg-secondary/40 rounded-xl p-3 space-y-3 w-72 shrink-0" data-testid={`kanban-column-${col.key}`}>
            <div className="flex items-center gap-2 px-1">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
              <button
                className={`text-xs font-semibold uppercase tracking-wider text-muted-foreground flex-1 text-left truncate ${col.parentTask ? "hover:text-primary" : "cursor-default"}`}
                onClick={() => col.parentTask && onSelect(col.parentTask.id)}>
                {col.label}
              </button>
              <span className="text-xs text-muted-foreground">{col.items.length}</span>
            </div>
            <Droppable droppableId={col.key} isDropDisabled={!dragEnabled}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                  className={`space-y-2 min-h-[40px] rounded-lg transition-colors ${snapshot.isDraggingOver ? "bg-accent/60" : ""}`}>
                  {col.items.map((t, i) => (
                    <KanbanCard key={t.id} task={t} subtasks={subtasksByParent[t.id] || []}
                      members={members} onSelect={(id) => onSelect(groupBy === "parent" ? col.parentTask.id : id)}
                      index={i} opts={opts} dragDisabled={!dragEnabled}
                      imageFile={imagesByTask[t.id]} />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            {groupBy === "status" && (quickAdd === col.key ? (
              <Input autoFocus value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)}
                data-testid={`kanban-quick-input-${col.key}`}
                onKeyDown={(e) => { if (e.key === "Enter") submitQuick(col.key); if (e.key === "Escape") setQuickAdd(null); }}
                onBlur={() => submitQuick(col.key)}
                placeholder="Título de la tarea..." className="h-8 text-sm bg-card" maxLength={300} />
            ) : (
              <button onClick={() => setQuickAdd(col.key)}
                data-testid={`kanban-quick-add-${col.key}`}
                className="w-full flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-1 py-1 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Añadir tarea
              </button>
            ))}
          </div>
        ))}
        </div>
      </div>
    </DragDropContext>
  );
};
